import { useEffect, useMemo, useState } from "react";
import { AdminRestaurantList } from "../../models/IRestaurantDetail";
import { getAdminRestaurantList } from "../../services/restaurant";
import { IReservation, IReservationScheduleItem, IRestaurantTable } from "../models/IReservation";
import { getPendingReservations, getReservationSchedule, getRestaurantTables } from "../services/reservationService";
import TableFormModal from "../components/TableFormModal";
import DeleteTableModal from "../components/DeleteTableModal";
import ConfirmReservationModal from "../components/ConfirmReservationModal";
import CreateReservationModal from "../components/CreateReservationModal";
import ReservationStatusModal from "../components/ReservationStatusModal";
import ReservationSettingsPanel from "../components/ReservationSettingsPanel";
import OccupancyPanel from "../components/OccupancyPanel";

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  Pending:   { label: "Bekliyor",   color: "#d97706", bg: "#fffbeb" },
  Confirmed: { label: "Onaylandı",  color: "#16a34a", bg: "#f0fdf4" },
  Cancelled: { label: "İptal",      color: "#dc2626", bg: "#fef2f2" },
  Rejected:  { label: "Reddedildi", color: "#dc2626", bg: "#fef2f2" },
  Completed: { label: "Tamamlandı", color: "#2563eb", bg: "#eff6ff" },
};

const SOURCE_LABEL: Record<string, string> = {
  CustomerForm: "Web Formu",
  Phone: "Telefon",
  Panel: "Panel / Kapı",
};

function getStatusStyle(status: string) {
  return STATUS_STYLE[status] ?? { label: status, color: "#6b7280", bg: "#f9fafb" };
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const todayStr = () => toDateInputValue(new Date());

type Tab = "reservations" | "tables" | "schedule" | "occupancy" | "settings";
type TableModalState = { mode: "add" } | { mode: "edit"; table: IRestaurantTable } | null;
type StatusModalState = {
  mode: "reject" | "cancel";
  reservation: { id: string; contactName: string; contactPhone: string; startsAtUtc: string; partySize: number };
} | null;

export default function AdminReservationManagementPage() {
  const [restaurantList, setRestaurantList] = useState<AdminRestaurantList>();
  const [selectedId, setSelectedId] = useState<string>("");
  const [tab, setTab] = useState<Tab>("reservations");

  const [reservations, setReservations] = useState<IReservation[]>([]);
  const [loadingReservations, setLoadingReservations] = useState(false);

  const [tables, setTables] = useState<IRestaurantTable[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);

  const [tableModal, setTableModal] = useState<TableModalState>(null);
  const [deletingTable, setDeletingTable] = useState<IRestaurantTable | null>(null);
  const [confirmingReservation, setConfirmingReservation] = useState<IReservation | null>(null);
  const [statusModal, setStatusModal] = useState<StatusModalState>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [scheduleDate, setScheduleDate] = useState(todayStr());
  const [schedule, setSchedule] = useState<IReservationScheduleItem[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  useEffect(() => {
    getAdminRestaurantList().then((res) => {
      if (res.data) setRestaurantList(res.data);
    });
  }, []);

  const loadReservations = (restaurantId: string) => {
    setLoadingReservations(true);
    getPendingReservations(restaurantId)
      .then((res) => setReservations(res.data ?? []))
      .catch(() => setReservations([]))
      .finally(() => setLoadingReservations(false));
  };

  const loadTables = (restaurantId: string) => {
    setLoadingTables(true);
    getRestaurantTables(restaurantId)
      .then((res) => setTables(res.data ?? []))
      .catch(() => setTables([]))
      .finally(() => setLoadingTables(false));
  };

  const loadSchedule = (restaurantId: string, date: string) => {
    setLoadingSchedule(true);
    const fromUtc = new Date(date + "T00:00:00Z").toISOString();
    const toUtc = new Date(date + "T23:59:59Z").toISOString();
    getReservationSchedule(restaurantId, fromUtc, toUtc)
      .then((res) => setSchedule(res.data ?? []))
      .catch(() => setSchedule([]))
      .finally(() => setLoadingSchedule(false));
  };

  useEffect(() => {
    if (!selectedId) {
      setReservations([]);
      setTables([]);
      setSchedule([]);
      return;
    }
    loadReservations(selectedId);
    loadTables(selectedId);
    loadSchedule(selectedId, scheduleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const scheduleByTable = useMemo(() => {
    const map = new Map<string, { tableNumber: number; tableLabel: string; items: IReservationScheduleItem[] }>();
    schedule.forEach((item) => {
      if (!map.has(item.tableId)) {
        map.set(item.tableId, { tableNumber: item.tableNumber, tableLabel: item.tableLabel, items: [] });
      }
      map.get(item.tableId)!.items.push(item);
    });
    return Array.from(map.values())
      .sort((a, b) => a.tableNumber - b.tableNumber)
      .map((g) => ({
        ...g,
        items: g.items.slice().sort((a, b) => new Date(a.startsAtUtc).getTime() - new Date(b.startsAtUtc).getTime()),
      }));
  }, [schedule]);

  return (
    <div style={{ padding: "24px", maxWidth: 960, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontWeight: 800, color: "#111827", marginBottom: 4 }}>
          <i className="bi bi-calendar-check-fill me-2" style={{ color: "#0d9488" }}></i>Online Rezervasyon Yönetimi
        </h4>
        <p style={{ color: "#6b7280", fontSize: "0.88rem", margin: 0 }}>
          Restoran seçin; bekleyen rezervasyonları, masaları ve rezervasyon programını buradan yönetin.
        </p>
      </div>

      {/* Restoran seçici + yeni rezervasyon */}
      <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 320px", maxWidth: 420 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <i className="bi bi-building me-1"></i>Restoran
          </label>
          <select
            className="form-select form-select-lg"
            style={{ borderRadius: 12, border: "1.5px solid #dee2e6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", fontWeight: 500 }}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="" disabled>Restoran seçiniz...</option>
            {restaurantList?.items.map((r) => (
              <option key={r.id} value={r.id}>{r.restourantName}</option>
            ))}
          </select>
        </div>

        {selectedId && (
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: "12px 20px", borderRadius: 12, border: "none",
              background: "linear-gradient(135deg, #0d9488, #0f766e)",
              color: "#fff", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
              boxShadow: "0 4px 14px rgba(13,148,136,0.3)",
            }}
          >
            <i className="bi bi-calendar-plus-fill"></i>Yeni Rezervasyon
          </button>
        )}
      </div>

      {!selectedId ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
          <i className="bi bi-shop" style={{ fontSize: "2.5rem", display: "block", marginBottom: 10 }}></i>
          <span style={{ fontSize: "0.9rem" }}>Görüntülemek için restoran seçin.</span>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, boxShadow: "inset 0 -1.5px 0 #e5e7eb", overflowX: "auto", whiteSpace: "nowrap" }}>
            <button
              onClick={() => setTab("reservations")}
              style={{
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "0.88rem",
                color: tab === "reservations" ? "#0d9488" : "#9ca3af",
                borderBottom: tab === "reservations" ? "2.5px solid #0d9488" : "2.5px solid transparent",
              }}
            >
              <i className="bi bi-hourglass-split me-1"></i>Bekleyen Rezervasyonlar
              {reservations.length > 0 && (
                <span style={{ marginLeft: 6, background: "#0d9488", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: "0.72rem" }}>
                  {reservations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab("tables")}
              style={{
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "0.88rem",
                color: tab === "tables" ? "#0d9488" : "#9ca3af",
                borderBottom: tab === "tables" ? "2.5px solid #0d9488" : "2.5px solid transparent",
              }}
            >
              <i className="bi bi-grid-3x3-gap-fill me-1"></i>Masalar
              {tables.length > 0 && (
                <span style={{ marginLeft: 6, background: "#0d9488", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: "0.72rem" }}>
                  {tables.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab("schedule")}
              style={{
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "0.88rem",
                color: tab === "schedule" ? "#0d9488" : "#9ca3af",
                borderBottom: tab === "schedule" ? "2.5px solid #0d9488" : "2.5px solid transparent",
              }}
            >
              <i className="bi bi-calendar-week-fill me-1"></i>Rezervasyon Programı
              {schedule.length > 0 && (
                <span style={{ marginLeft: 6, background: "#0d9488", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: "0.72rem" }}>
                  {schedule.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab("occupancy")}
              style={{
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "0.88rem",
                color: tab === "occupancy" ? "#0d9488" : "#9ca3af",
                borderBottom: tab === "occupancy" ? "2.5px solid #0d9488" : "2.5px solid transparent",
              }}
            >
              <i className="bi bi-pie-chart-fill me-1"></i>Doluluk
            </button>
            <button
              onClick={() => setTab("settings")}
              style={{
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "0.88rem",
                color: tab === "settings" ? "#0d9488" : "#9ca3af",
                borderBottom: tab === "settings" ? "2.5px solid #0d9488" : "2.5px solid transparent",
              }}
            >
              <i className="bi bi-gear-fill me-1"></i>Ayarlar
            </button>
          </div>

          {/* Bekleyen rezervasyonlar */}
          {tab === "reservations" && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <button
                  onClick={() => loadReservations(selectedId)}
                  style={{ padding: "6px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer" }}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>Yenile
                </button>
              </div>

              {loadingReservations ? (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                  <div className="spinner-border" style={{ color: "#0d9488" }} role="status" />
                </div>
              ) : reservations.length === 0 ? (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#9ca3af" }}>
                  <i className="bi bi-calendar-x" style={{ fontSize: "2.2rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
                  <span style={{ fontSize: "0.88rem" }}>Bekleyen rezervasyon bulunmuyor.</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {reservations.map((r) => {
                    const status = getStatusStyle(r.status);
                    return (
                      <div key={r.id} style={{
                        borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "14px 18px",
                      }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#111827" }}>
                              {r.contactName}
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: 2 }}>
                              <i className="bi bi-telephone-fill me-1"></i>{r.contactPhone}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: status.bg, color: status.color }}>
                              {status.label}
                            </span>
                            <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#f3f4f6", color: "#374151" }}>
                              {SOURCE_LABEL[r.source] ?? r.source}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 12 }}>
                          <div>
                            <div style={{ fontSize: "0.68rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Tarih / Saat</div>
                            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>
                              {formatDateTime(r.startsAtUtc)} — {formatTime(r.endsAtUtc)}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.68rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Kişi Sayısı</div>
                            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>
                              <i className="bi bi-people-fill me-1"></i>{r.partySize}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.68rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Oluşturulma</div>
                            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>
                              {formatDateTime(r.createdAtUtc)}
                            </div>
                          </div>
                        </div>

                        {r.note && (
                          <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 10, background: "#f9fafb", fontSize: "0.82rem", color: "#4b5563" }}>
                            <i className="bi bi-chat-square-text me-1"></i>{r.note}
                          </div>
                        )}

                        {r.status === "Pending" && (
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                            <button
                              onClick={() => setStatusModal({ mode: "reject", reservation: r })}
                              style={{ padding: "7px 16px", borderRadius: 10, border: "1.5px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}
                            >
                              <i className="bi bi-x-lg me-1"></i>Reddet
                            </button>
                            <button
                              onClick={() => setConfirmingReservation(r)}
                              style={{ padding: "7px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #0d9488, #0f766e)", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}
                            >
                              <i className="bi bi-check2-circle me-1"></i>Onayla
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Masalar */}
          {tab === "tables" && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8 }}>
                <button
                  onClick={() => loadTables(selectedId)}
                  style={{ padding: "6px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer" }}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>Yenile
                </button>
                <button
                  onClick={() => setTableModal({ mode: "add" })}
                  style={{ padding: "6px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #0d9488, #0f766e)", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}
                >
                  <i className="bi bi-plus-lg me-1"></i>Masa Ekle
                </button>
              </div>

              {loadingTables ? (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                  <div className="spinner-border" style={{ color: "#0d9488" }} role="status" />
                </div>
              ) : tables.length === 0 ? (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#9ca3af" }}>
                  <i className="bi bi-grid" style={{ fontSize: "2.2rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
                  <span style={{ fontSize: "0.88rem" }}>Henüz masa eklenmemiş.</span>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
                  {tables
                    .slice()
                    .sort((a, b) => a.tableNumber - b.tableNumber)
                    .map((t) => (
                      <div key={t.id} style={{
                        borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "14px 16px",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{
                            width: 30, height: 30, borderRadius: 8, background: "#f0fdfa", color: "#0d9488",
                            display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.85rem",
                          }}>
                            {t.tableNumber}
                          </span>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.isEnabled ? "#10b981" : "#f87171" }} />
                        </div>
                        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827", marginTop: 10 }}>
                          {t.label}
                        </div>
                        <div style={{ fontSize: "0.76rem", color: "#6b7280", marginTop: 4 }}>
                          <i className="bi bi-people-fill me-1"></i>{t.minCapacity}–{t.capacity} kişi
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                          <button
                            onClick={() => setTableModal({ mode: "edit", table: t })}
                            style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.76rem", cursor: "pointer" }}
                          >
                            <i className="bi bi-pencil-fill me-1"></i>Düzenle
                          </button>
                          <button
                            onClick={() => setDeletingTable(t)}
                            style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "1.5px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontWeight: 600, fontSize: "0.76rem", cursor: "pointer" }}
                          >
                            <i className="bi bi-trash-fill me-1"></i>Sil
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}

          {/* Rezervasyon Programı */}
          {tab === "schedule" && (
            <>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap" }}>
                <div>
                  <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Tarih</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="form-control"
                    style={{ borderRadius: 10, border: "1.5px solid #e5e7eb" }}
                  />
                </div>
                <button
                  onClick={() => loadSchedule(selectedId, scheduleDate)}
                  style={{ padding: "8px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #0d9488, #0f766e)", color: "#fff", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}
                >
                  <i className="bi bi-search me-1"></i>Uygula
                </button>
              </div>

              {loadingSchedule ? (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                  <div className="spinner-border" style={{ color: "#0d9488" }} role="status" />
                </div>
              ) : scheduleByTable.length === 0 ? (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#9ca3af" }}>
                  <i className="bi bi-calendar-week" style={{ fontSize: "2.2rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
                  <span style={{ fontSize: "0.88rem" }}>Seçilen tarihte onaylı rezervasyon bulunmuyor.</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {scheduleByTable.map((group) => (
                    <div key={group.tableNumber} style={{
                      borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "14px 18px",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            width: 28, height: 28, borderRadius: 8, background: "#f0fdfa", color: "#0d9488",
                            display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.8rem", flexShrink: 0,
                          }}>
                            {group.tableNumber}
                          </span>
                          <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>{group.tableLabel}</span>
                        </div>
                        <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#f3f4f6", color: "#374151" }}>
                          {group.items.length} rezervasyon
                        </span>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {group.items.map((item) => {
                          const status = getStatusStyle(item.status);
                          return (
                            <div key={item.id} style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
                              padding: "8px 12px", borderRadius: 10, background: "#f9fafb",
                            }}>
                              <div>
                                <div style={{ fontSize: "0.84rem", fontWeight: 700, color: "#374151" }}>
                                  {formatDateTime(item.startsAtUtc)} — {formatTime(item.endsAtUtc)}
                                </div>
                                <div style={{ fontSize: "0.76rem", color: "#6b7280", marginTop: 2 }}>
                                  {item.contactName}
                                  {item.contactPhone && (
                                    <>
                                      {" · "}
                                      <a href={`tel:${item.contactPhone}`} style={{ color: "inherit", textDecoration: "none" }}>
                                        <i className="bi bi-telephone-fill me-1"></i>{item.contactPhone}
                                      </a>
                                    </>
                                  )}
                                  {" · "}<i className="bi bi-people-fill me-1"></i>{item.partySize} kişi
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: status.bg, color: status.color }}>
                                  {status.label}
                                </span>
                                {item.status === "Confirmed" && (
                                  <button
                                    onClick={() => setStatusModal({
                                      mode: "cancel",
                                      reservation: {
                                        id: item.id, contactName: item.contactName, contactPhone: item.contactPhone,
                                        startsAtUtc: item.startsAtUtc, partySize: item.partySize,
                                      },
                                    })}
                                    style={{ padding: "4px 10px", borderRadius: 8, border: "1.5px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}
                                  >
                                    <i className="bi bi-calendar-x me-1"></i>İptal Et
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Doluluk */}
          {tab === "occupancy" && (
            <OccupancyPanel restaurantId={selectedId} />
          )}

          {/* Ayarlar */}
          {tab === "settings" && (
            <ReservationSettingsPanel restaurantId={selectedId} />
          )}
        </>
      )}

      {tableModal && (
        <TableFormModal
          restaurantId={selectedId}
          table={tableModal.mode === "edit" ? tableModal.table : undefined}
          onClose={() => setTableModal(null)}
          onSaved={() => loadTables(selectedId)}
        />
      )}

      {deletingTable && (
        <DeleteTableModal
          table={deletingTable}
          onClose={() => setDeletingTable(null)}
          onDeleted={() => loadTables(selectedId)}
        />
      )}

      {confirmingReservation && (
        <ConfirmReservationModal
          reservation={confirmingReservation}
          onClose={() => setConfirmingReservation(null)}
          onConfirmed={() => {
            loadReservations(selectedId);
            loadSchedule(selectedId, scheduleDate);
          }}
        />
      )}

      {statusModal && (
        <ReservationStatusModal
          reservation={statusModal.reservation}
          mode={statusModal.mode}
          onClose={() => setStatusModal(null)}
          onDone={() => {
            loadReservations(selectedId);
            loadSchedule(selectedId, scheduleDate);
          }}
        />
      )}

      {showCreateModal && selectedId && (
        <CreateReservationModal
          restaurantId={selectedId}
          onClose={() => setShowCreateModal(false)}
          onCreated={(startsAtUtc) => {
            // Rezervasyon ileri bir tarihteyse program o güne geçsin, kayıt gözden kaçmasın
            const day = toDateInputValue(new Date(startsAtUtc));
            setScheduleDate(day);
            loadSchedule(selectedId, day);
            loadReservations(selectedId);
            setTab("schedule");
          }}
        />
      )}
    </div>
  );
}
